/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useUserId } from '@/api/hooks';
import {
  getUserSettings,
  saveUserSettings,
  type HomePreference,
  type ThemeSetting,
} from '@/api/settings';
import { useUiStore } from '@/stores/uiStore';
import { useTheme } from './useTheme';

/**
 * Light/dark theme toggle that also persists the choice to the backend.
 *
 * The startup sync (useSyncUserSettings) restores the theme from the server rather than
 * localStorage, so a toggle that only wrote localStorage was reverted on the next reload. Saving
 * the new theme here keeps the server as the source of truth and makes the preference stick. The
 * user's other saved settings (plant, language, home preference) are passed through unchanged.
 */
export function useThemeToggle(): { resolved: 'light' | 'dark'; toggle: () => void } {
  const { resolved } = useTheme();
  const setTheme = useUiStore((s) => s.setTheme);
  const gmin = useUserId();

  const settingsQuery = useQuery({
    queryKey: ['user-settings', gmin],
    queryFn: () => getUserSettings(gmin),
    enabled: Boolean(gmin),
    retry: false,
  });

  const toggle = useCallback(() => {
    const next: ThemeSetting = resolved === 'dark' ? 'light' : 'dark';
    setTheme(next);
    if (!gmin) return;

    const s = settingsQuery.data;
    // Avoid overwriting unrelated user settings with hardcoded defaults if settings haven't loaded yet.
    if (!s) return;

    void saveUserSettings(gmin, {
      plant: s.plant.plantCode,
      language: s.languageDisplay.languageCode,
      preference: (s.preferenceCode === 'manufacturer' ? 'manufacturer' : 'dealer') as HomePreference,
      theme: next,
    }).catch(() => {
      // Best-effort persistence; localStorage still holds the in-session choice.
    });
  }, [resolved, setTheme, gmin, settingsQuery.data]);

  return { resolved, toggle };
}
